import { AmountSuperScript } from "components/design/AmountSuperScript";
import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import BarePriceInput from "components/PriceInput/BarePriceInput";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useUpdateCartItemAmountMutation } from "store/apis/products";
import { BackendError } from "types/utils/error";

type CartItemAmountUpdateProps = {
  cartItemId: string;
  unitPrice: number;
  entityId: string;
};

export const CartItemAmountUpdate = ({
  cartItemId,
  unitPrice,
  entityId,
}: CartItemAmountUpdateProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(String(unitPrice));
  const { alertToast, successToast, info } = useToast();
  const [updateAmount, { isLoading }] = useUpdateCartItemAmountMutation();

  const handleSave = async () => {
    const parsed = parseFloat(value);
    if (isNaN(parsed) || parsed === unitPrice) {
      setIsEditing(false);
      setValue(String(unitPrice));
      info({ message: "Amount is unchanged, nothing to update" });
      return;
    }
    try {
      await updateAmount({ entityId, cartItemId, unitPrice: parsed }).unwrap();
      successToast({ message: "Amount updated successfully" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
      setValue(String(unitPrice));
    } finally {
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") {
      setValue(String(unitPrice));
      setIsEditing(false);
    }
  };

  if (isLoading) {
    return <Loader size="small" addClassName="t-mr-1" />;
  }

  if (isEditing) {
    return (
      <div className="t-w-24">
        <BarePriceInput
          customSize="small"
          value={value}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  }

  return (
    <Button
      customType="ghost"
      size="small"
      onClick={() => {
        setValue(String(unitPrice));
        setIsEditing(true);
      }}
    >
      <AmountSuperScript amount={unitPrice} />
    </Button>
  );
};
